import { FormattedMessage } from 'react-intl'

import { Badge } from '@gto/ui/badge'
import { Typography } from '@gto/ui/typography'

import type { LedgerReadinessStatus } from '@/utils/ledger-search-params'

type BadgeColor = 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'

/**
 * Colour + label per readiness bucket. `NO_PAYOUT_REQUESTED` is the headline
 * alert — money is collectable but the partner has not asked — so it gets the
 * prominent warning tone. Missing method/details are hard blockers (error);
 * a pending request is informational; below-minimum is neutral.
 */
const READINESS_CONFIG: Record<LedgerReadinessStatus, { color: BadgeColor; labelId: string }> = {
  NO_PAYOUT_REQUESTED: { color: 'warning', labelId: 'admin.ledger.readiness.noPayoutRequested' },
  MISSING_METHOD: { color: 'error', labelId: 'admin.ledger.readiness.missingMethod' },
  MISSING_DETAILS: { color: 'error', labelId: 'admin.ledger.readiness.missingDetails' },
  PENDING_PAYOUT: { color: 'info', labelId: 'admin.ledger.readiness.pendingPayout' },
  BELOW_MINIMUM: { color: 'secondary', labelId: 'admin.ledger.readiness.belowMinimum' },
}

const isReadinessStatus = (value: string): value is LedgerReadinessStatus =>
  Object.prototype.hasOwnProperty.call(READINESS_CONFIG, value)

export const LedgerReadinessChip = ({ status }: { status: string }) => {
  if (!isReadinessStatus(status)) {
    return (
      <Typography
        color="text.disabled"
        variant="body2"
      >
        —
      </Typography>
    )
  }
  const { color, labelId } = READINESS_CONFIG[status]

  return (
    <span className="inline-flex items-center gap-2">
      <Badge
        color={color}
        variant="dot"
      />
      <Typography
        className="text-[0.75rem] leading-[1]"
        variant="body2"
      >
        <FormattedMessage id={labelId} />
      </Typography>
    </span>
  )
}
